/**
 * SupervisedChildLoginModal.jsx
 * 
 * Modal for supervised child PIN login (Art Ping Pong)
 * Shows list of supervised children (fetched from parent's Firestore doc) and PIN entry
 */

import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { db } from '../services/firebase';
import { doc, getDoc } from 'firebase/firestore';
import './SupervisedChildLoginModal.css';

const SupervisedChildLoginModal = ({ isOpen, onClose, onSuccess }) => {
  const { currentUser, signInSupervisedChild } = useAuth();
  const [supervisedChildren, setSupervisedChildren] = useState([]);
  const [selectedChild, setSelectedChild] = useState(null);
  const [pin, setPin] = useState('');
  const [loading, setLoading] = useState(true);
  const [signingIn, setSigningIn] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (isOpen && currentUser) {
      loadSupervisedChildren();
    }
  }, [isOpen, currentUser]);

  const loadSupervisedChildren = async () => {
    if (!currentUser) {
      setError('Please sign in as a parent first');
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError(null);

      // Get parent's user document
      const userDoc = await getDoc(doc(db, 'users', currentUser.uid));
      if (!userDoc.exists()) {
        setError('User profile not found');
        setLoading(false);
        return;
      }

      const userData = userDoc.data();
      const childUids = userData.supervisedChildren || [];

      if (childUids.length === 0) {
        setError('No supervised children found. Create one in DoMoreWorld Settings.');
        setLoading(false);
        return;
      }

      // Fetch supervised children profiles
      const childrenData = [];
      for (const childUid of childUids) {
        const childDoc = await getDoc(doc(db, 'users', childUid));
        if (childDoc.exists()) {
          childrenData.push({ id: childDoc.id, ...childDoc.data() });
        }
      }

      setSupervisedChildren(childrenData);
      setLoading(false);
    } catch (err) {
      console.error('Error loading supervised children:', err);
      setError('Failed to load supervised children. Please try again.');
      setLoading(false);
    }
  };

  const handlePinInput = (value) => {
    // Only allow digits, max 6 characters
    const digits = value.replace(/\D/g, '').slice(0, 6);
    setPin(digits);
  };

  const handleSignIn = async (e) => {
    e.preventDefault();

    if (!selectedChild) {
      setError('Please select a child');
      return;
    }

    if (pin.length !== 6) {
      setError('Please enter a 6-digit PIN');
      return;
    }

    setSigningIn(true);
    setError(null);

    try {
      await signInSupervisedChild(selectedChild.id, pin);
      console.log('✅ Supervised child signed in');
      if (onSuccess) {
        onSuccess();
      }
      onClose();
    } catch (err) {
      console.error('Error signing in:', err);
      // Parse error message for user-friendly display
      let errorMsg = 'Failed to sign in. Please check the PIN and try again.';
      if (err.message?.includes('locked')) {
        errorMsg = err.message; // Show lockout message from backend
      } else if (err.message?.includes('Incorrect PIN')) {
        errorMsg = 'Incorrect PIN. Please try again.';
      } else if (err.message?.includes('Account locked')) {
        errorMsg = err.message;
      }
      setError(errorMsg);
      setPin(''); // Clear PIN on error
    } finally {
      setSigningIn(false);
    }
  };

  const handleBack = () => {
    setSelectedChild(null);
    setPin('');
    setError(null);
  };

  if (!isOpen) return null;

  return (
    <div className="modal-overlay-app" onClick={onClose}>
      <div className="modal-container-app supervised-child-login-modal-app" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header-app">
          <h2>🧒 Sign In as Child</h2>
          <button onClick={onClose} className="close-button-app">×</button>
        </div>

        <div className="modal-body-app">
          {loading ? (
            <div className="loading-state-app">
              <div className="loading-spinner-app"></div>
              <p>Loading children...</p>
            </div>
          ) : error && supervisedChildren.length === 0 ? (
            <div className="error-state-app">
              <div className="error-icon-app">⚠️</div>
              <p>{error}</p>
              <button onClick={onClose} className="btn-app btn-secondary-app">
                Close
              </button>
            </div>
          ) : !selectedChild ? (
            // Step 1: Select Child
            <div className="child-selection-app">
              <p className="step-description-app">
                Select which child is signing in:
              </p>
              <div className="children-list-app">
                {supervisedChildren.map(child => (
                  <button
                    key={child.id}
                    onClick={() => setSelectedChild(child)}
                    className="child-button-app"
                  >
                    <span className="child-icon-app">🧒</span>
                    <span className="child-name-app">{child.displayName}</span>
                  </button>
                ))}
              </div>
              {error && (
                <div className="error-message-app">
                  ⚠️ {error}
                </div>
              )}
            </div>
          ) : (
            // Step 2: Enter PIN
            <form onSubmit={handleSignIn} className="pin-form-app">
              <div className="selected-child-info-app">
                <div className="child-icon-large-app">🧒</div>
                <h3>{selectedChild.displayName}</h3>
              </div>

              <p className="step-description-app">
                Enter your 6-digit PIN:
              </p>

              <div className="pin-input-group-app">
                <span className="lock-icon-app">🔒</span>
                <input
                  type="password"
                  inputMode="numeric"
                  pattern="[0-9]*"
                  value={pin}
                  onChange={(e) => handlePinInput(e.target.value)}
                  placeholder="••••••"
                  className="pin-input-app"
                  maxLength={6}
                  autoFocus
                />
                <span className="pin-length-app">{pin.length}/6</span>
              </div>

              {error && (
                <div className="error-message-app">
                  ⚠️ {error}
                </div>
              )}

              <div className="button-group-app">
                <button 
                  type="button" 
                  onClick={handleBack} 
                  className="btn-app btn-secondary-app"
                  disabled={signingIn}
                >
                  Back
                </button>
                <button 
                  type="submit" 
                  className="btn-app btn-primary-app"
                  disabled={pin.length !== 6 || signingIn}
                >
                  {signingIn ? 'Signing In...' : 'Sign In'}
                </button>
              </div>
            </form>
          )}
        </div>
      </div>
    </div>
  );
};

export default SupervisedChildLoginModal;
